import React, { useState, useEffect, useCallback } from 'react';
import { kbAPI } from '../services/api';

function CategoryManager({ listFn, createFn, updateFn, deleteFn, reorderFn, title }) {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState('');
    const [newParentId, setNewParentId] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [editingParentId, setEditingParentId] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try { setCategories((await listFn()).data); }
        catch (err) { setError(err.response?.data?.error || err.message); }
        finally { setLoading(false); }
    }, [listFn]);

    useEffect(() => { load(); }, [load]);

    const parents = categories.filter(c => !c.parentId);
    const childrenOf = (pid) => categories.filter(c => c.parentId === pid);

    const add = async () => {
        if (!newName.trim()) return;
        setError('');
        try {
            await createFn({ name: newName.trim(), parentId: newParentId || null });
            setNewName('');
            setNewParentId('');
            load();
        } catch (err) { setError(err.response?.data?.error || err.message); }
    };

    const saveEdit = async (id) => {
        if (!editingName.trim()) return;
        setError('');
        try {
            await updateFn(id, {
                name: editingName.trim(),
                parentId: editingParentId === '' ? null : parseInt(editingParentId, 10),
            });
            setEditingId(null);
            load();
        } catch (err) { setError(err.response?.data?.error || err.message); }
    };

    const remove = async (id) => {
        const cat = categories.find(c => c.id === id);
        const hasKids = cat && childrenOf(id).length > 0;
        const msg = hasKids
            ? 'Delete this category? Its subcategories will become top-level, and articles in it will become uncategorized.'
            : 'Delete this category? Articles in it will become uncategorized.';
        if (!window.confirm(msg)) return;
        try { await deleteFn(id); load(); }
        catch (err) { setError(err.response?.data?.error || err.message); }
    };

    const move = async (id, dir) => {
        const target = categories.find(c => c.id === id);
        if (!target) return;
        const siblings = categories.filter(c => (c.parentId || null) === (target.parentId || null));
        const idx = siblings.findIndex(c => c.id === id);
        const j = idx + dir;
        if (j < 0 || j >= siblings.length) return;
        const swapped = [...siblings];
        [swapped[idx], swapped[j]] = [swapped[j], swapped[idx]];
        const sibSet = new Set(siblings.map(s => s.id));
        let sibPtr = 0;
        const next = categories.map(c => sibSet.has(c.id) ? swapped[sibPtr++] : c);
        setCategories(next);
        try { await reorderFn(next.map(c => c.id)); }
        catch (err) { setError(err.response?.data?.error || err.message); load(); }
    };

    const renderRow = (c, isChild) => {
        const kids = childrenOf(c.id);
        const siblings = categories.filter(x => (x.parentId || null) === (c.parentId || null));
        const idx = siblings.findIndex(x => x.id === c.id);
        const cannotBecomeChild = kids.length > 0;
        const parentOptions = parents.filter(p => p.id !== c.id);

        return (
            <div key={c.id} className={`kb-admin-row ${isChild ? 'kb-admin-child' : ''}`}>
                <div className="kb-admin-reorder">
                    <button onClick={() => move(c.id, -1)} disabled={idx === 0} className="kb-admin-arrow">▲</button>
                    <button onClick={() => move(c.id, 1)} disabled={idx === siblings.length - 1} className="kb-admin-arrow">▼</button>
                </div>

                {editingId === c.id ? (
                    <>
                        <input
                            type="text"
                            className="form-input"
                            style={{ flex: 1 }}
                            value={editingName}
                            onChange={e => setEditingName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && saveEdit(c.id)}
                            autoFocus
                        />
                        <select
                            className="form-select"
                            style={{ width: 'auto' }}
                            value={editingParentId}
                            onChange={e => setEditingParentId(e.target.value)}
                            disabled={cannotBecomeChild}
                            title={cannotBecomeChild ? 'Has subcategories — move or delete them first' : ''}
                        >
                            <option value="">— Top level —</option>
                            {parentOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <button className="btn btn-primary btn-sm" onClick={() => saveEdit(c.id)}>Save</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                    </>
                ) : (
                    <>
                        <div className="kb-admin-cat-info">
                            <div className="kb-admin-cat-name">
                                {isChild && <span className="kb-admin-child-arrow">↳ </span>}
                                {c.name}
                            </div>
                            <div className="kb-admin-cat-sub">
                                {c.articleCount} item{c.articleCount === 1 ? '' : 's'}
                                {kids.length > 0 && ` · ${kids.length} subcategor${kids.length === 1 ? 'y' : 'ies'}`}
                            </div>
                        </div>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => { setEditingId(c.id); setEditingName(c.name); setEditingParentId(c.parentId ? String(c.parentId) : ''); }}
                        >Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => remove(c.id)}>Delete</button>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="card" style={{ marginBottom: 28 }}>
            <div className="card-header">
                <span className="card-title">{title}</span>
            </div>
            <div className="card-body">
                {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
                <div className="kb-admin-add-row">
                    <input
                        type="text"
                        className="form-input"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && add()}
                        placeholder="New category name"
                        style={{ flex: 1 }}
                    />
                    <select className="form-select" style={{ width: 'auto' }} value={newParentId} onChange={e => setNewParentId(e.target.value)}>
                        <option value="">— Top-level —</option>
                        {parents.map(p => <option key={p.id} value={p.id}>Subcategory of: {p.name}</option>)}
                    </select>
                    <button className="btn btn-primary" onClick={add}>Add</button>
                </div>
                {loading ? (
                    <div className="loading"><div className="spinner"></div></div>
                ) : categories.length === 0 ? (
                    <div className="empty-state"><div className="empty-state-text">No categories yet.</div></div>
                ) : (
                    <div className="kb-admin-list">
                        {parents.map(p => (
                            <React.Fragment key={p.id}>
                                {renderRow(p, false)}
                                {childrenOf(p.id).map(k => renderRow(k, true))}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function KnowledgeBaseAdmin() {
    return (
        <div className="container fade-in">
            <div className="page-header">
                <div>
                    <h1>Knowledge Base Categories</h1>
                    <p>Manage categories for articles and recipes</p>
                </div>
            </div>
            <CategoryManager
                title="Knowledge Base Categories"
                listFn={kbAPI.listCategories}
                createFn={kbAPI.createCategory}
                updateFn={kbAPI.updateCategory}
                deleteFn={kbAPI.deleteCategory}
                reorderFn={kbAPI.reorderCategories}
            />
            <CategoryManager
                title="Recipe Categories"
                listFn={kbAPI.listRecipeCategories}
                createFn={kbAPI.createRecipeCategory}
                updateFn={kbAPI.updateRecipeCategory}
                deleteFn={kbAPI.deleteRecipeCategory}
                reorderFn={kbAPI.reorderRecipeCategories}
            />
        </div>
    );
}

export default KnowledgeBaseAdmin;
